import React, { useEffect, useRef, useState } from 'react';
const mojs = require("@mojs/core");

interface HeartButtonProps {
  onClick?: (e: React.MouseEvent) => void;
  active?: boolean; 
}

const AnimatedHeartButton: React.FC<HeartButtonProps> = ({
  onClick,
  active = false,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconRef = useRef<SVGSVGElement>(null);
  const [popKey, setPopKey] = useState(0);
  const moTimeline = useRef<any>(null);
  const moburst1 = useRef<any>(null);
  const moburst2 = useRef<any>(null);
  const moBubbles = useRef<any>(null);

  useEffect(() => {
    if (!buttonRef.current) return;

    const button = buttonRef.current;
    // size the burst from the icon, not the padded tap target
    const rect = (iconRef.current ?? button).getBoundingClientRect();
    const buttonSize = Math.max(rect.width, rect.height);

    const timeline = new mojs.Timeline();
    
    moburst1.current = new mojs.Burst({
      parent: button,
      count: 10,
      radius: { 0: buttonSize * 2.5 },
      duration: 1500,
      children: {
        radius: { 15: 0 },
        easing: 'cubic.out',
        degreeShift: 'rand(-50, 50)',
      }
    });

    moburst2.current = new mojs.Burst({
      parent: button,
      count: 15,
      radius: { 0: buttonSize * 2 },
      children: {
        shape: 'line',
        stroke: '#FF4136',
        fill: 'none',
        scale: 1,
        scaleX: { 1: 0 },
        easing: 'cubic.out',
        duration: 1000,
        degreeShift: 'rand(-50, 50)',
      }
    });

    moBubbles.current = new mojs.Burst({
      parent: button,
      radius: buttonSize * 1.5,
      count: 5,
      timeline: { delay: 200 },
      children: {
        stroke: '#FF4136',
        fill: 'none',
        scale: 1,
        strokeWidth: { 8: 0 },
        radius: { 0: 'rand(6, 10)' },
        degreeShift: 'rand(-50, 50)',
        duration: 400,
        delay: 'rand(0, 250)',
      }
    });

    const circleOpts = {
      parent: button,
      radius: { 0: buttonSize * 1.8 },
      duration: 750,
      shape: 'circle',
      fill: 'none',
      stroke: '#FF4136',
      strokeWidth: 1,
      opacity: { 1: 0 }
    };

    const circle1 = new mojs.Shape({
      ...circleOpts
    });

    const circle2 = new mojs.Shape({
      ...circleOpts,
      delay: 100
    });

    timeline.add(circle1, circle2);
    moTimeline.current = timeline;

    return () => {
      timeline.reset();
    };
  }, []);
  
    const handleClick = (e: React.MouseEvent) => {
      if (onClick) {
        onClick(e);
      }

      if (!active) setPopKey((k) => k + 1);

      if (moburst1.current && moburst2.current && moBubbles.current && moTimeline.current) {
        moburst1.current
          .generate()
          .replay();
        
        moburst2.current
          .generate()
          .replay();
        
        moBubbles.current
          .generate()
          .replay();
        
        moTimeline.current.replay();
      }
    };
  
    return (
      <button
        ref={buttonRef}
        onClick={handleClick}
        className={`relative flex items-center justify-center w-12 h-11 rounded-full text-muted transition-transform duration-200 [@media(hover:hover)]:hover:scale-110 active:scale-90 focus:outline-none focus-visible:outline-periwinkle ${
          active ? 'active' : ''
        }`}
        aria-label="Like"
        aria-pressed={active}
      >
        <svg
          ref={iconRef}
          key={popKey}
          className={popKey > 0 && active ? 'animate-heart-pop' : ''}
          width="22"
          height="18"
          viewBox="0 0 128 128"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className="transition-colors duration-250"
            d="M64.425 19.75l-.46-.462c-13.72-13.717-35.96-13.717-49.677 0C.57 33.006.57 55.246 14.288 68.964l49.676 49.676.46-.46.46.46 49.677-49.676c13.72-13.718 13.72-35.958 0-49.676-13.715-13.717-35.955-13.717-49.673 0l-.46.46z"
            stroke={active ? "#FF4136" : "currentColor"}
            strokeWidth="10"
            fill={active ? "#FF4136" : "transparent"}
            fillRule="evenodd"
          />
        </svg>
      </button>
    );
  };
  
  export default AnimatedHeartButton;